import type {
  Absence,
  BankHoliday,
  Booking,
  BookingType,
  Resource,
  WorkingHoursProfile,
} from '@/types';
import {
  calculateDayCapacity,
  type DayCapacity,
  getWorkingHoursForDay,
  normalizeDurationType,
  resolveBookingEnd,
} from '@/utils/heatmap-utils';

const MS_PER_HOUR = 1000 * 60 * 60;

export interface HeatmapTick {
  start: Date;
  end: Date;
}

export interface HeatmapTickInput {
  resource: Resource;
  tick: HeatmapTick;
  /** Bookings already narrowed to the row (resource + project / customer grouping) */
  bookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
  absences?: Absence[];
  bankHolidays?: BankHoliday[];
  bookingTypes?: BookingType[];
  /** Hour of day the working window opens; only used by sub-day ticks */
  businessHoursStart?: number;
  dayCapacityCache?: Map<string, DayCapacity>;
}

export interface HeatmapTickUtilisation {
  /** Working-profile hours covered by the tick, before absence / bank-holiday loss */
  workingHours: number;
  /** workingHours minus the hours removed by absences and bank holidays */
  availableHours: number;
  bookedHours: number;
  confirmedHours: number;
  plannedHours: number;
  absenceHours: number;
  /** bookedHours / availableHours × 100, capped at 999 */
  utilisation: number;
  /** Two non-cancelled bookings with genuinely overlapping times */
  hasHardConflict: boolean;
  /** Every booking overlapping the tick, cancelled ones included (tooltip list) */
  matchedBookings: Booking[];
}

const startOfDayLocal = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Overlap between two [start, end] hour-of-day windows, in hours. */
const windowOverlapHours = (
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): number => Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));

/** Working-profile hours the tick covers, ignoring absences and bank holidays. */
export function calculateTickWorkingHours(
  resource: Resource,
  tick: HeatmapTick,
  globalWorkingProfile: WorkingHoursProfile,
  businessHoursStart = 9,
): number {
  const tickHours = (tick.end.getTime() - tick.start.getTime()) / MS_PER_HOUR;

  if (tickHours >= 24) {
    let hours = 0;
    const day = new Date(tick.start);
    while (day < tick.end) {
      hours += getWorkingHoursForDay(resource, day, globalWorkingProfile);
      day.setDate(day.getDate() + 1);
    }
    return hours;
  }

  const dayHours = getWorkingHoursForDay(resource, tick.start, globalWorkingProfile);
  if (dayHours === 0) return 0;

  const tickStartHour = tick.start.getHours() + tick.start.getMinutes() / 60;
  return windowOverlapHours(
    tickStartHour,
    tickStartHour + tickHours,
    businessHoursStart,
    businessHoursStart + dayHours,
  );
}

/**
 * Hours a single booking consumes inside a tick.
 *
 * Day-based bookings (full-day / half-day) consume the resource's profile
 * hours for each day they cover.  On sub-day ticks (day view) those hours are
 * spread across the working window the booking occupies — a full-day booking
 * fills the whole window, half-day AM the first half and half-day PM the
 * second — so an hour-long tick reports its own hour rather than the whole
 * day's hours.  Hourly bookings always use their real overlap with the tick.
 */
export function calculateBookedHoursForTick(
  booking: Booking,
  resource: Resource,
  tick: HeatmapTick,
  globalWorkingProfile: WorkingHoursProfile,
  businessHoursStart = 9,
): number {
  const durationType = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
  const isFullDay = durationType === 'full-day';
  const isHalfDay = durationType.startsWith('half-day');
  const bookingStart = new Date(booking.startTime);
  const bookingEnd = resolveBookingEnd(booking, isFullDay || isHalfDay);

  if (!(bookingStart < tick.end && bookingEnd > tick.start)) return 0;

  const tickHours = (tick.end.getTime() - tick.start.getTime()) / MS_PER_HOUR;

  if (!isFullDay && !isHalfDay) {
    const overlapStart = bookingStart > tick.start ? bookingStart : tick.start;
    const overlapEnd = bookingEnd < tick.end ? bookingEnd : tick.end;
    return Math.max(0, (overlapEnd.getTime() - overlapStart.getTime()) / MS_PER_HOUR);
  }

  // A day-based booking covers every calendar day its span overlaps, matching
  // the day loop in calculateBookedMinutesForDay.
  const coversDay = (dayStart: Date): boolean => {
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    return bookingStart < dayEnd && bookingEnd > dayStart;
  };

  if (tickHours >= 24) {
    let hours = 0;
    const day = new Date(tick.start);
    while (day < tick.end) {
      if (coversDay(day)) {
        const dayHours = getWorkingHoursForDay(resource, day, globalWorkingProfile);
        hours += isFullDay ? dayHours : dayHours / 2;
      }
      day.setDate(day.getDate() + 1);
    }
    return hours;
  }

  // Sub-day tick: spread the booking over the window it occupies so the tick
  // only reports the hours that actually fall inside it.
  if (!coversDay(startOfDayLocal(tick.start))) return 0;

  const dayHours = getWorkingHoursForDay(resource, tick.start, globalWorkingProfile);
  if (dayHours === 0) return 0;

  const tickStartHour = tick.start.getHours() + tick.start.getMinutes() / 60;
  const tickEndHour = tickStartHour + tickHours;
  const windowStart = businessHoursStart;
  const windowEnd = businessHoursStart + dayHours;
  const middayHour = businessHoursStart + dayHours / 2;

  if (isFullDay) {
    return windowOverlapHours(tickStartHour, tickEndHour, windowStart, windowEnd);
  }
  if (durationType === 'half-day-am') {
    return windowOverlapHours(tickStartHour, tickEndHour, windowStart, middayHour);
  }
  if (durationType === 'half-day-pm') {
    return windowOverlapHours(tickStartHour, tickEndHour, middayHour, windowEnd);
  }
  // Half day with no AM/PM period — spread evenly across the whole window.
  return windowOverlapHours(tickStartHour, tickEndHour, windowStart, windowEnd) / 2;
}

/**
 * Utilisation of one heatmap cell (one resource row × one timeline tick).
 *
 *   Utilisation % = Booked Hours / Available Hours × 100
 *
 * Bank holidays and absences reduce Available Hours; they are never counted as
 * booked time.  Every booked hour counts towards the numerator, so overlapping
 * bookings stack above 100% (two full-day bookings on a 7h day = 200%).
 */
export function calculateHeatmapTickUtilisation({
  resource,
  tick,
  bookings,
  globalWorkingProfile,
  absences = [],
  bankHolidays = [],
  bookingTypes,
  businessHoursStart = 9,
  dayCapacityCache,
}: HeatmapTickInput): HeatmapTickUtilisation {
  const workingHours = calculateTickWorkingHours(resource, tick, globalWorkingProfile, businessHoursStart);
  const tickHours = (tick.end.getTime() - tick.start.getTime()) / MS_PER_HOUR;

  const matchedBookings: Booking[] = [];
  let bookedHours = 0;
  let confirmedHours = 0;
  let plannedHours = 0;

  bookings.forEach((booking) => {
    const durationType = normalizeDurationType(booking.durationType, booking.halfDayPeriod);
    const isDayBased = durationType === 'full-day' || durationType.startsWith('half-day');
    const bookingStart = new Date(booking.startTime);
    const bookingEnd = resolveBookingEnd(booking, isDayBased);
    if (!(bookingStart < tick.end && bookingEnd > tick.start)) return;

    matchedBookings.push(booking);
    if (booking.status === 'cancelled') return;

    const hours = calculateBookedHoursForTick(
      booking, resource, tick, globalWorkingProfile, businessHoursStart,
    );
    bookedHours += hours;
    if (booking.status === 'confirmed' || booking.status === 'completed') {
      confirmedHours += hours;
    } else {
      plannedHours += hours;
    }
  });

  matchedBookings.sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );

  // Hours removed by absences / bank holidays, using the shared day capacity
  // helper so the reduction matches calculatePeriodUtilization exactly.
  let absenceHours = 0;
  const day = startOfDayLocal(tick.start);
  while (day < tick.end) {
    const dayHours = getWorkingHoursForDay(resource, day, globalWorkingProfile);
    if (dayHours > 0) {
      const dayKey = `${String(resource.id)}:${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
      const cachedCapacity = dayCapacityCache?.get(dayKey);
      const dayCapacity = cachedCapacity ?? calculateDayCapacity(
        String(resource.id), day, bookings, dayHours, absences, bankHolidays, bookingTypes,
      );
      if (!cachedCapacity) {
        dayCapacityCache?.set(dayKey, dayCapacity);
      }
      absenceHours += dayCapacity.lostMinutes / 60;
    }
    day.setDate(day.getDate() + 1);
  }
  // Sub-day ticks only see a slice of the day's capacity, so scale the loss.
  if (tickHours < 24) {
    const dayHours = getWorkingHoursForDay(resource, tick.start, globalWorkingProfile);
    absenceHours = dayHours > 0 ? absenceHours * (workingHours / dayHours) : 0;
  }

  const availableHours = Math.max(0, workingHours - absenceHours);

  let hasHardConflict = false;
  const active = matchedBookings.filter((b) => b.status !== 'cancelled');
  outer: for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const aStart = new Date(active[i].startTime);
      const aEnd = new Date(active[i].endTime);
      const bStart = new Date(active[j].startTime);
      const bEnd = new Date(active[j].endTime);
      if (aStart < bEnd && aEnd > bStart) {
        hasHardConflict = true;
        break outer;
      }
    }
  }

  return {
    workingHours,
    availableHours,
    bookedHours,
    confirmedHours,
    plannedHours,
    absenceHours,
    utilisation: availableHours > 0 ? Math.min(999, (bookedHours / availableHours) * 100) : 0,
    hasHardConflict,
    matchedBookings,
  };
}
